import React from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

// The six well-known violation classes emitted by the crypto backend's narrative gates
// (see internal/reports/narrative_readback.go). Kept in sync with contract-pack §8.2.
const VIOLATION_LABELS: Record<string, string> = {
  number_unverified: 'Number not in math-core',
  sentence_unverified_drop: 'Sentence dropped (unverified)',
  vol_window_mislabel: 'Volatility window mislabelled',
  directive_language: 'Directive language removed',
  template_internal_leak: 'Template internal leaked',
  internal_codename: 'Internal codename scrubbed',
  label_drift: 'Non-canonical pillar/tool label',
  selector_mismatch: 'Narrative selector disagrees with composite bridge',
};

export interface NarrativeReadbackViolation {
  class: string;
  detail?: string;
}

export interface NarrativeReadbackShape {
  status: 'PASS' | 'FAIL' | string;
  violations?: NarrativeReadbackViolation[];
}

interface Props {
  readback?: NarrativeReadbackShape | null;
  className?: string;
}

/**
 * Small pill that renders the narrative-readback status returned by the backend.
 * PASS: green — the six scrub gates found nothing to fix.
 * FAIL: amber — one or more gates fired; click the row to see per-violation class + detail.
 * When no readback is present (older payload, or ai_summary skipped) the badge is hidden entirely,
 * not shown as "unknown" — an unknown state would misrepresent the contract.
 */
const NarrativeReadbackBadge: React.FC<Props> = ({ readback, className }) => {
  const [open, setOpen] = React.useState(false);
  if (!readback || (readback.status !== 'PASS' && readback.status !== 'FAIL')) {
    return null;
  }
  const isPass = readback.status === 'PASS';
  const count = readback.violations?.length ?? 0;
  const base = 'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-mono';
  const passClass = 'bg-emerald-900/30 text-emerald-300 border border-emerald-800/60';
  const failClass = 'bg-amber-900/30 text-amber-300 border border-amber-800/60 cursor-pointer hover:bg-amber-900/50';
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => !isPass && setOpen((v) => !v)}
        className={`${base} ${isPass ? passClass : failClass}`}
        title={isPass ? 'All six narrative-scrub gates cleared this summary.' : 'Click to view violations captured by the narrative gates.'}
      >
        {isPass ? <ShieldCheck size={12} /> : <AlertTriangle size={12} />}
        <span>Narrative readback · {isPass ? 'PASS' : `FAIL · ${count} violation${count === 1 ? '' : 's'}`}</span>
      </button>
      {!isPass && open && (
        <div className="mt-2 rounded border border-amber-800/60 bg-cmd-panel p-2 text-[11px] font-mono text-cmd-textDim">
          <div className="mb-1 text-amber-300">Violations captured by the scrub gates (LLM output was cleaned before publish):</div>
          <ul className="space-y-1">
            {(readback.violations ?? []).map((v, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-400">•</span>
                <span>
                  <span className="text-cmd-text">{VIOLATION_LABELS[v.class] ?? v.class}</span>
                  {v.detail ? <span className="text-cmd-textDim"> — {v.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NarrativeReadbackBadge;
